import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  CalendarX2, Plus, Trash2, Pencil, AlertTriangle, Check,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { formatDateFr } from "../lib/dateFormat";
import { KIND_LABELS } from "./NonWorkingDayBanner";

const KINDS = [
  { value: "holiday", label: "Férié", color: "#DC2626" },
  { value: "annual_closure", label: "Fermeture annuelle", color: "#7C3AED" },
  { value: "exceptional_closure", label: "Fermeture exceptionnelle", color: "#D97706" },
];

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/**
 * Onglet "Jours non travaillés" — affiché dans Départements & structure (admin).
 *
 * Fonctionnalités :
 *  - Liste chronologique des entrées (toutes brandes confondues) avec édition/suppression.
 *  - Création multi-département (checkboxes + case "Tous").
 *  - Aperçu rétroactif : nombre de shifts déjà planifiés ce jour-là.
 */
export default function NonWorkingDaysTab({ brands, canManage = true }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    date: todayISO(),
    label: "",
    kind: "holiday",
    brand_ids: [],
    all_brands: false,
  });
  const [conflictInfo, setConflictInfo] = useState({ total: 0, by_brand: {} });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/non-working-days");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Regroupe par (date + label) pour éviter les doublons visuels dans la liste
  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of items) {
      const key = `${e.date}||${e.label}||${e.kind}`;
      if (!map.has(key)) map.set(key, { date: e.date, label: e.label, kind: e.kind, color: e.color, entries: [] });
      map.get(key).entries.push(e);
    }
    return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
  }, [items]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      date: todayISO(),
      label: "",
      kind: "holiday",
      brand_ids: [],
      all_brands: false,
    });
    setConflictInfo({ total: 0, by_brand: {} });
    setDialogOpen(true);
  };

  const openEditGroup = (group) => {
    // On édite l'entrée (label/kind) — la portée département est figée pour un groupe
    // (on n'ajoute/retire pas de brands via l'édition ; il faut supprimer/recréer).
    setEditing(group);
    setForm({
      date: group.date,
      label: group.label,
      kind: group.kind,
      brand_ids: group.entries.map((e) => e.brand_id),
      all_brands: false,
    });
    setConflictInfo({ total: 0, by_brand: {} });
    setDialogOpen(true);
  };

  // Récupère l'alerte "shifts existants" à chaque changement de date/brands
  useEffect(() => {
    if (!dialogOpen || editing) return;
    const brand_ids = form.all_brands ? "all" : form.brand_ids.join(",");
    if (!brand_ids || !form.date) { setConflictInfo({ total: 0, by_brand: {} }); return; }
    const controller = new AbortController();
    (async () => {
      try {
        const { data } = await api.get("/non-working-days/conflicts", {
          params: { brand_ids, date: form.date },
          signal: controller.signal,
        });
        setConflictInfo({ total: data.total_shifts || 0, by_brand: data.by_brand || {} });
      } catch {
        setConflictInfo({ total: 0, by_brand: {} });
      }
    })();
    return () => controller.abort();
  }, [dialogOpen, editing, form.date, form.brand_ids, form.all_brands]);

  const submit = async () => {
    if (!form.label.trim()) { toast.error("Libellé requis"); return; }
    if (!form.date) { toast.error("Date requise"); return; }
    const targets = form.all_brands ? "all" : form.brand_ids;
    if (!form.all_brands && form.brand_ids.length === 0) {
      toast.error("Sélectionnez au moins un département"); return;
    }
    try {
      if (editing) {
        // Édition = mise à jour de chaque entrée du groupe (label + kind)
        await Promise.all(editing.entries.map((e) =>
          api.put(`/non-working-days/${e.id}`, { label: form.label.trim(), kind: form.kind })));
        toast.success("Jour non travaillé mis à jour");
      } else {
        const { data } = await api.post("/non-working-days", {
          brand_ids: targets,
          date: form.date,
          label: form.label.trim(),
          kind: form.kind,
        });
        const existing = data?.existing_shifts || {};
        const totalExisting = Object.values(existing).reduce((a, n) => a + n, 0);
        if (totalExisting > 0) {
          toast.warning(`Créé — ${totalExisting} shift${totalExisting > 1 ? "s" : ""} existant${totalExisting > 1 ? "s" : ""} sur cette date`, { duration: 8000 });
        } else {
          toast.success(`Créé sur ${data?.created || 0} département${(data?.created || 0) > 1 ? "s" : ""}`);
        }
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (group) => {
    if (!window.confirm(`Supprimer "${group.label}" (${group.entries.length} département${group.entries.length > 1 ? "s" : ""}) ?`)) return;
    try {
      await Promise.all(group.entries.map((e) => api.delete(`/non-working-days/${e.id}`)));
      toast.success("Supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const toggleBrand = (bid) => {
    setForm((f) => {
      const has = f.brand_ids.includes(bid);
      return {
        ...f,
        brand_ids: has ? f.brand_ids.filter((x) => x !== bid) : [...f.brand_ids, bid],
        all_brands: false,
      };
    });
  };

  return (
    <div className="space-y-4" data-testid="non-working-days-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-zinc-600">
            Fériés, fermetures annuelles ou exceptionnelles.
            Les récurrences (shifts et interventions) ignoreront automatiquement ces jours.
          </p>
        </div>
        {canManage && (
          <Button onClick={openCreate} className="bg-[#7C3AED] hover:bg-[#6B29CC] rounded-sm" data-testid="nwd-create-btn">
            <Plus size={14} className="mr-1" /> Nouveau jour non travaillé
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-sm text-zinc-500 font-mono">Chargement…</p>
      ) : grouped.length === 0 ? (
        <div className="border border-dashed border-zinc-300 rounded-sm p-8 text-center" data-testid="nwd-empty">
          <CalendarX2 size={28} className="mx-auto text-zinc-400 mb-2" />
          <p className="text-sm text-zinc-600 font-medium">Aucun jour non travaillé enregistré</p>
          <p className="text-xs text-zinc-500 mt-1">
            Utilisez « Nouveau jour non travaillé » pour ajouter fériés et fermetures.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {grouped.map((g) => {
            const kind = KINDS.find((k) => k.value === g.kind) || KINDS[2];
            const brandNames = g.entries
              .map((e) => brands.find((b) => b.id === e.brand_id)?.name)
              .filter(Boolean);
            return (
              <div
                key={`${g.date}-${g.label}`}
                className="border border-zinc-200 rounded-sm p-3 flex items-center gap-3 bg-white hover:bg-zinc-50"
                style={{ borderLeft: `4px solid ${g.color || kind.color}` }}
                data-testid={`nwd-row-${g.date}`}
              >
                <CalendarX2 size={18} style={{ color: g.color || kind.color }} />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm text-zinc-900 truncate">{g.label}</p>
                  <p className="text-xs text-zinc-500 font-mono">
                    {formatDateFr(g.date)} · {KIND_LABELS[g.kind]}
                  </p>
                  <p className="text-[11px] text-zinc-600 mt-1 truncate">
                    Départements&nbsp;: <span className="font-medium">{brandNames.join(" · ") || "—"}</span>
                    <span className="text-zinc-400 ml-1.5">({g.entries.length})</span>
                  </p>
                </div>
                {canManage && (
                  <>
                    <button
                      onClick={() => openEditGroup(g)}
                      className="text-xs px-2 py-1 rounded-sm border border-zinc-200 hover:bg-white text-zinc-700 inline-flex items-center gap-1"
                      data-testid={`nwd-edit-${g.date}`}
                    >
                      <Pencil size={12} /> Modifier
                    </button>
                    <button
                      onClick={() => remove(g)}
                      className="text-xs px-2 py-1 rounded-sm border border-red-200 text-red-700 hover:bg-red-50 inline-flex items-center gap-1"
                      data-testid={`nwd-delete-${g.date}`}
                    >
                      <Trash2 size={12} /> Supprimer
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="nwd-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {editing ? "Modifier le jour non travaillé" : "Nouveau jour non travaillé"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  disabled={!!editing}
                  className="rounded-sm"
                  data-testid="nwd-date-input"
                />
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <select
                  value={form.kind}
                  onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
                  className="w-full h-10 rounded-sm border border-zinc-300 bg-white px-3 text-sm"
                  data-testid="nwd-kind-select"
                >
                  {KINDS.map((k) => (
                    <option key={k.value} value={k.value}>{k.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Libellé</Label>
              <Input
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="ex. Noël, Pont de l'Ascension, Fermeture d'été…"
                className="rounded-sm"
                data-testid="nwd-label-input"
              />
            </div>

            {!editing && (
              <div>
                <Label className="text-xs mb-1.5 block">Départements concernés</Label>
                <label className="flex items-center gap-2 mb-2 cursor-pointer">
                  <Checkbox
                    checked={form.all_brands}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, all_brands: !!v, brand_ids: v ? [] : f.brand_ids }))}
                    data-testid="nwd-all-brands"
                  />
                  <span className="text-sm font-medium">Tous les départements</span>
                </label>
                {!form.all_brands && (
                  <div className="grid grid-cols-2 gap-1.5 max-h-40 overflow-auto border border-zinc-200 rounded-sm p-2">
                    {brands.map((b) => (
                      <label key={b.id} className="flex items-center gap-2 cursor-pointer text-sm px-1 py-0.5 hover:bg-zinc-50">
                        <Checkbox
                          checked={form.brand_ids.includes(b.id)}
                          onCheckedChange={() => toggleBrand(b.id)}
                          data-testid={`nwd-brand-${b.id}`}
                        />
                        <span className="inline-flex items-center gap-1.5 truncate">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: b.color }} />
                          <span className="truncate">{b.name}</span>
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}

            {editing && (
              <div className="rounded-sm bg-zinc-50 border border-zinc-200 p-2 text-xs text-zinc-600">
                Portée figée&nbsp;: <span className="font-medium">{editing.entries.length}</span> département(s).
                Pour ajouter/retirer un département, supprimez cette entrée et recréez-en une.
              </div>
            )}

            {!editing && conflictInfo.total > 0 && (
              <div
                className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2"
                data-testid="nwd-conflict-alert"
              >
                <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold">
                    {conflictInfo.total} shift{conflictInfo.total > 1 ? "s" : ""} déjà planifié{conflictInfo.total > 1 ? "s" : ""} ce jour-là
                  </p>
                  <p className="mt-0.5">
                    {Object.entries(conflictInfo.by_brand).map(([, v]) => `${v.name} (${v.count})`).join(" · ")}
                  </p>
                  <p className="mt-1 text-amber-800">
                    Ces shifts seront conservés — pensez à les vérifier manuellement.
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">
              Annuler
            </Button>
            <Button onClick={submit} className="bg-[#7C3AED] hover:bg-[#6B29CC] rounded-sm" data-testid="nwd-submit">
              <Check size={14} className="mr-1" /> {editing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
